import { queryOptions } from '@tanstack/react-query'
import type {
  CreateProjectInput,
  ListProjectsResult,
  Project,
  UpdateProjectInput,
} from '@terafile/shared'
import { apiFetch } from './api.js'

/**
 * プロジェクト関連の API クライアント関数群。
 * すべて `apiFetch` 経由で JWT 自動付与・共通エラー形式の取り扱い。
 */

export type ListProjectsParams = {
  archived?: boolean
  page?: number
  limit?: number
}

async function listProjects(params: ListProjectsParams = {}): Promise<ListProjectsResult> {
  const usp = new URLSearchParams()
  if (params.archived !== undefined) usp.set('archived', String(params.archived))
  if (params.page !== undefined) usp.set('page', String(params.page))
  if (params.limit !== undefined) usp.set('limit', String(params.limit))
  const qs = usp.toString()
  return apiFetch<ListProjectsResult>(`/projects${qs ? `?${qs}` : ''}`)
}

/** 一覧を一度に取得する件数。フォルダ単位のページングはしていない（ADR 0006 の MVP 範囲） */
const PROJECT_LIST_LIMIT = 100

/**
 * プロジェクト一覧のクエリ定義。
 *
 * メイン画面とサイドバーのツリーが同じキャッシュを読む。キーや件数が食い違うと
 * 別々のキャッシュになり、プロジェクトの移動・作成が片方にしか反映されなくなるので、
 * 両者ともこの関数経由で useQuery すること。
 */
export function projectListQueryOptions(archived: boolean) {
  return queryOptions({
    queryKey: ['projects', { archived }] as const,
    queryFn: () => listProjects({ archived, limit: PROJECT_LIST_LIMIT }),
  })
}

export async function getProject(id: string): Promise<Project> {
  return apiFetch<Project>(`/projects/${id}`)
}

export async function createProject(input: CreateProjectInput): Promise<Project> {
  return apiFetch<Project>('/projects', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function updateProject(id: string, input: UpdateProjectInput): Promise<Project> {
  return apiFetch<Project>(`/projects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export async function archiveProject(id: string): Promise<Project> {
  return apiFetch<Project>(`/projects/${id}/archive`, { method: 'POST' })
}

export async function unarchiveProject(id: string): Promise<Project> {
  return apiFetch<Project>(`/projects/${id}/unarchive`, { method: 'POST' })
}

export async function deleteProject(id: string, confirmation: string): Promise<void> {
  await apiFetch<{ id: string }>(`/projects/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ confirmation }),
  })
}
